import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface MultiOpt {
  value: string
  label: string
  /** Optional right-aligned meta (e.g. a YTD figure) shown muted on each row. */
  meta?: string
}

/** Lightweight labelled MULTI-select dropdown — the multi-pick sibling of Dropdown.
 *  Stays open while toggling; closes on outside-click / Esc. The trigger summarises
 *  the selection (All / name / "N classes") and rings champagne when non-empty. */
export function MultiDropdown({
  label,
  options,
  selected,
  onChange,
  onClear,
  onSelectAll,
  allLabel = 'All',
  searchThreshold = 12,
  align = 'left',
}: {
  label: string
  options: MultiOpt[]
  selected: string[]
  onChange: (next: string[]) => void
  onClear: () => void
  onSelectAll?: () => void
  allLabel?: string
  searchThreshold?: number
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setQ('')
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v
  const triggerText =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? labelFor(selected[0])
        : selected.length <= 3
          ? selected.map(labelFor).join(', ')
          : `${selected.length} selected`

  const showSearch = options.length > searchThreshold
  const shown = showSearch && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 transition-colors hover:bg-stone-50 dark:bg-stone-900 dark:hover:bg-stone-800',
          selected.length
            ? 'border-champagne-300 ring-1 ring-champagne-300 dark:border-champagne-500/50 dark:ring-champagne-500/40'
            : 'border-stone-200 dark:border-stone-800',
        )}
      >
        <span className="text-[10px] font-medium uppercase tracking-wider text-stone-400">{label}</span>
        <span className="max-w-[11rem] truncate text-xs font-medium text-stone-700 dark:text-stone-200">{triggerText}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-stone-400" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className={cn(
            'absolute z-30 mt-1 max-h-72 w-72 overflow-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg dark:border-stone-800 dark:bg-stone-900',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-stone-100 bg-white px-3 py-1.5 dark:border-stone-800 dark:bg-stone-900">
            <div className="flex gap-3 text-xs">
              <button type="button" className="text-stone-500 transition-colors hover:text-stone-800 dark:hover:text-stone-200" onClick={onClear}>
                Clear
              </button>
              {onSelectAll ? (
                <button type="button" className="text-stone-500 transition-colors hover:text-stone-800 dark:hover:text-stone-200" onClick={onSelectAll}>
                  Select all
                </button>
              ) : null}
            </div>
            <span className="text-xs text-stone-400">{selected.length} selected</span>
          </div>
          {showSearch ? (
            <div className="px-2 py-1.5">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                aria-label={`Search ${label.toLowerCase()}`}
                className="w-full rounded-md border border-stone-200 bg-white px-2 py-1 text-xs text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-champagne-300 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-200"
              />
            </div>
          ) : null}
          {shown.map((o) => {
            const on = selected.includes(o.value)
            return (
              <button
                key={o.value}
                role="option"
                aria-selected={on}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-stone-50 dark:hover:bg-stone-800"
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border',
                    on ? 'border-champagne-500 bg-champagne-500 text-white' : 'border-stone-300 dark:border-stone-600',
                  )}
                >
                  {on ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
                </span>
                <span className={cn('flex-1 truncate', on ? 'font-medium text-stone-900 dark:text-white' : 'text-stone-600 dark:text-stone-300')}>
                  {o.label}
                </span>
                {o.meta ? <span className="shrink-0 tabular-nums text-stone-400">{o.meta}</span> : null}
              </button>
            )
          })}
          {shown.length === 0 ? <p className="px-3 py-2 text-xs text-stone-400">No matches.</p> : null}
        </div>
      )}
    </div>
  )
}
